
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitCompare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import FilterSection from '@/components/football/FilterSection';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SavedState, MatchData } from '@/components/team-details/types/selector-types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const CompareSection = () => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [homeTeam, setHomeTeam] = useState<string | null>(null);
  const [awayTeam, setAwayTeam] = useState<string | null>(null);

  // Fetch saved states
  const { data: savedStates } = useQuery({
    queryKey: ['saved-states'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('saved_states')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as SavedState[];
    }
  });

  // Fetch leagues data to get the correct order
  const { data: leaguesData } = useQuery({
    queryKey: ['leagues'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leagues')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  const availableCountries = Array.from(new Set(savedStates?.map(state => state.country) || [])).sort();

  // Get available leagues and sort them based on the leagues table display_order
  const availableLeagues = React.useMemo(() => {
    if (!savedStates || !leaguesData || !selectedCountry) return [];

    // Get leagues for selected country
    const countryLeagues = Array.from(new Set(savedStates.filter(state => state.country === selectedCountry).map(state => state.league))).filter(Boolean) as string[];

    // Get the ordered leagues data for the selected country
    const countryLeaguesData = leaguesData.filter(league => countryLeagues.includes(league.name)).sort((a, b) => (a.display_order || 999) - (b.display_order || 999));

    // Map to just the league names in order
    const orderedLeagues = countryLeaguesData.map(league => league.name);

    // Add any remaining leagues that aren't in the leagues table
    const remainingLeagues = countryLeagues.filter(league => !orderedLeagues.includes(league));
    return [...orderedLeagues, ...remainingLeagues];
  }, [savedStates, selectedCountry, leaguesData]);

  const availableYears = Array.from(new Set(savedStates?.filter(state => (!selectedCountry || state.country === selectedCountry) && (!selectedLeague || state.league === selectedLeague))?.map(state => state.year) || [])).sort((a, b) => b.localeCompare(a)); // Sort years in descending order

  const availableTeams = React.useMemo(() => {
    if (!selectedLeague || !selectedYear) return [];
    const state = savedStates?.find(state => state.league === selectedLeague && state.year === selectedYear);
    if (!state?.data) return [];
    const teams = new Set<string>();
    (state.data as MatchData[]).forEach(match => {
      if (match.HomeTeam) teams.add(match.HomeTeam);
      if (match.AwayTeam) teams.add(match.AwayTeam);
    });
    return Array.from(teams).sort();
  }, [savedStates, selectedLeague, selectedYear]);

  const handleCompare = () => {
    if (homeTeam && awayTeam && selectedLeague && selectedYear) {
      navigate('/compare', {
        state: {
          homeTeam,
          awayTeam,
          league: selectedLeague,
          year: selectedYear
        }
      });
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow w-full max-w-md h-full">
      <CardHeader className="text-center">
        <GitCompare className="w-24 h-24 mx-auto mb-4" />
        <CardTitle className="text-2xl">Compare Teams</CardTitle>
        <CardDescription className="text-lg">
          Compare Match statistics between two teams
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="w-full max-w-xs mx-auto">
          <FilterSection
            selectedCountry={selectedCountry}
            selectedLeague={selectedLeague}
            selectedYear={selectedYear}
            onCountrySelect={setSelectedCountry}
            onLeagueSelect={setSelectedLeague}
            onYearSelect={setSelectedYear}
            leagues={availableLeagues}
            years={availableYears}
          />

          {selectedYear && (
            <div className="space-y-4 mt-4">
              <select
                className="w-full p-2 border rounded"
                value={homeTeam || ''}
                onChange={(e) => setHomeTeam(e.target.value)}
              >
                <option value="">Select Home Team</option>
                {availableTeams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>

              <select
                className="w-full p-2 border rounded"
                value={awayTeam || ''}
                onChange={(e) => setAwayTeam(e.target.value)}
              >
                <option value="">Select Away Team</option>
                {availableTeams.filter(team => team !== homeTeam).map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>

              <Button
                className="w-full"
                onClick={handleCompare}
                disabled={!homeTeam || !awayTeam}
              >
                Compare Teams
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompareSection;
